/**
 * GoogleDrivePicker Component
 *
 * Uses Google Picker API to allow users to select files from their Google Drive.
 * The selected file is then imported to S3 via the backend.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { HardDrive, Loader } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { uploads } from '@/services/api/admin';

// Google API configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GOOGLE_PICKER_API_KEY = process.env.REACT_APP_GOOGLE_PICKER_API_KEY;

// Scopes required for Drive access
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';

// Supported MIME types
const SUPPORTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-msvideo',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

/**
 * GoogleDrivePicker - Select and import files from Google Drive
 *
 * @param {Object} props
 * @param {Function} props.onFileSelected - Callback with import result {url, resource_type, filename, content_type}
 * @param {Function} [props.onError] - Callback for errors
 * @param {boolean} [props.disabled] - Whether the picker is disabled
 * @param {string} [props.className] - Additional CSS classes
 */
export function GoogleDrivePicker({
  onFileSelected,
  onError,
  disabled = false,
  className = '',
}) {
  const [loading, setLoading] = useState(false);
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [gisLoaded, setGisLoaded] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [tokenClient, setTokenClient] = useState(null);

  // Use ref to store access token for use in picker callback (avoids stale closure)
  const accessTokenRef = useRef(null);

  // Load Google APIs on mount
  useEffect(() => {
    loadGoogleApis();
  }, []);

  const loadGoogleApis = () => {
    // Load gapi (Google API client)
    if (!window.gapi) {
      const gapiScript = document.createElement('script');
      gapiScript.src = 'https://apis.google.com/js/api.js';
      gapiScript.async = true;
      gapiScript.defer = true;
      gapiScript.onload = () => {
        window.gapi.load('picker', () => {
          setGapiLoaded(true);
        });
      };
      document.body.appendChild(gapiScript);
    } else {
      window.gapi.load('picker', () => {
        setGapiLoaded(true);
      });
    }

    // Load GIS (Google Identity Services)
    if (!window.google?.accounts) {
      const gisScript = document.createElement('script');
      gisScript.src = 'https://accounts.google.com/gsi/client';
      gisScript.async = true;
      gisScript.defer = true;
      gisScript.onload = () => {
        setGisLoaded(true);
      };
      document.body.appendChild(gisScript);
    } else {
      setGisLoaded(true);
    }
  };

  // Initialize token client when GIS is loaded
  useEffect(() => {
    if (gisLoaded && window.google?.accounts?.oauth2) {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: SCOPES,
        callback: (tokenResponse) => {
          if (tokenResponse.access_token) {
            setAccessToken(tokenResponse.access_token);
            accessTokenRef.current = tokenResponse.access_token;
            createPicker(tokenResponse.access_token);
          }
        },
      });
      setTokenClient(client);
    }
  }, [gisLoaded]);

  // Create and show the picker
  const createPicker = useCallback((token) => {
    if (!window.gapi?.picker || !token) {
      return;
    }

    const view = new window.google.picker.DocsView()
      .setIncludeFolders(false)
      .setSelectFolderEnabled(false);

    // Video view
    const videoView = new window.google.picker.DocsView()
      .setMimeTypes('video/mp4,video/webm,video/quicktime')
      .setIncludeFolders(false);

    // Image view
    const imageView = new window.google.picker.DocsView()
      .setMimeTypes('image/jpeg,image/png,image/gif,image/webp')
      .setIncludeFolders(false);

    const picker = new window.google.picker.PickerBuilder()
      .addView(view)
      .addView(videoView)
      .addView(imageView)
      .setOAuthToken(token)
      .setDeveloperKey(GOOGLE_PICKER_API_KEY)
      .setCallback(handlePickerCallback)
      .setTitle('Select a file from Google Drive')
      .build();

    picker.setVisible(true);
  }, []);

  // Handle picker selection
  const handlePickerCallback = async (data) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const file = data.docs[0];

      // Validate MIME type
      if (!SUPPORTED_TYPES.includes(file.mimeType)) {
        const error = `Unsupported file type: ${file.mimeType}. Please select an image, video, or document.`;
        if (onError) onError(error);
        return;
      }

      setLoading(true);

      try {
        // Import file from Drive to S3
        // Use ref to get current token (avoids stale closure issue)
        const currentToken = accessTokenRef.current;
        if (!currentToken) {
          throw new Error('No access token available. Please try again.');
        }

        const result = await uploads.importFromDrive({
          file_id: file.id,
          access_token: currentToken,
          filename: file.name,
          mime_type: file.mimeType,
        });

        onFileSelected({
          url: result.url,
          resource_type: result.resource_type,
          filename: result.filename,
          content_type: result.content_type,
        });
      } catch (err) {
        // Handle various error formats (Axios error, Error object, string, etc.)
        const errorMessage = err?.response?.data?.detail
          || err?.response?.data?.message
          || err?.message
          || (typeof err === 'string' ? err : 'Failed to import file from Google Drive');
        if (onError) onError(errorMessage);
      } finally {
        setLoading(false);
      }
    } else if (data.action === window.google.picker.Action.CANCEL) {
      // User cancelled - do nothing
    }
  };

  // Handle button click
  const handleClick = () => {
    if (!gapiLoaded || !gisLoaded) {
      if (onError) onError('Google APIs are still loading. Please try again.');
      return;
    }

    if (!GOOGLE_CLIENT_ID) {
      if (onError) onError('Google Client ID is not configured.');
      return;
    }

    if (!GOOGLE_PICKER_API_KEY) {
      if (onError) onError('Google Picker API Key is not configured.');
      return;
    }

    // Request access token (will trigger picker creation via callback)
    if (tokenClient) {
      // If we already have a token, just show the picker
      if (accessToken) {
        accessTokenRef.current = accessToken;  // Ensure ref is set
        createPicker(accessToken);
      } else {
        // Request a new token
        tokenClient.requestAccessToken({ prompt: '' });
      }
    }
  };

  const isReady = gapiLoaded && gisLoaded && tokenClient;

  return (
    <Button
      type="button"
      variant="outline"
      onClick={handleClick}
      disabled={disabled || loading || !isReady}
      className={className}
    >
      {loading ? (
        <>
          <Loader className="h-4 w-4 mr-2 animate-spin" />
          Importing...
        </>
      ) : (
        <>
          <HardDrive className="h-4 w-4 mr-2" />
          {isReady ? 'Import from Drive' : 'Loading...'}
        </>
      )}
    </Button>
  );
}

export default GoogleDrivePicker;
